import React, { forwardRef } from 'react';
import { useFormikContext, Field } from 'formik';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import InputType from './input-type';

const Input = forwardRef(
  (
    {
      name,
      id,
      type,
      children,
      cascadeErrorStatus,
      labelSize,
      labelHiddenText,
      label,
      required,
      ...rest
    },
    ref
  ) => {
    const { errors } = useFormikContext();
    let cascadedErrorStatus = false;

    if (cascadeErrorStatus) {
      const matches = name.match(/(.*)\[.*\]/);
      if (matches) {
        const parent = matches[1];

        if (
          errors[parent] &&
          (typeof errors[parent] === 'string' ||
            typeof errors[parent]._ === 'string')
        ) {
          cascadedErrorStatus = true;
        }
      }
    }

    return (
      <Field name={name}>
        {({ field, form, meta }) => {
          const { touched } = meta;
          const { status, setStatus, setFieldValue } = form;
          const { onChange, onBlur, value } = field;

          /**
           * Server errors are stored in the top level "status" field
           * https://stackoverflow.com/a/54328567
           */
          const error = meta.error || get(status, `errors.${name}`);

          const inputProps = {
            name,
            error: touched && (!!error || cascadedErrorStatus),
            id: id || name,
            value,
            onChange: (e) => {
              onChange(e);
              if (status?.errors) {
                const editStatusErrors = { ...status.errors };
                delete editStatusErrors[name];
                setStatus({ errors: editStatusErrors });
              }
            },
            onBlur,
            setFieldValue,
            required,
            ...rest,
          };

          const labelProps = {
            name,
            id,
            required,
            labelSize,
            label,
            labelHiddenText,
          };

          if (type) {
            return (
              <InputType
                type={type}
                inputProps={inputProps}
                labelProps={labelProps}
                value={field.value || field.initialValue}
                ref={ref}
              />
            );
          }

          if (typeof children === 'function') {
            return children({
              ...inputProps,
              ref,
            });
          }

          return React.cloneElement(React.Children.only(children), {
            ...inputProps,
            ref,
          });
        }}
      </Field>
    );
  }
);

Input.displayName = 'Input';

Input.propTypes = {
  name: PropTypes.string,
  id: PropTypes.string,
  type: PropTypes.string,
  children: PropTypes.element,
  cascadeErrorStatus: PropTypes.bool,
  labelSize: PropTypes.oneOf(['heading', 'standard']),
  labelHiddenText: PropTypes.string,
  label: PropTypes.string,
  required: PropTypes.bool,
};

export default Input;
